import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { getProjectFeedback, createProjectFeedback } from '@services/projects.service';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_FEEDBACK_VIEW, PROJECT_FEEDBACK_CREATE } from '@/ability';
import { isDiscussionContentEmpty } from './projectHelpers';
import { invalidateProjectQueries } from './projectQueryInvalidation';

export const PROJECT_FEEDBACK_QUERY_KEY = (projectId) => ['projects', projectId, 'feedback'];

/**
 * Build and validate feedback payload from form values. Returns { content, rating } or null if invalid.
 * Used so the tab does not contain validation logic.
 */
export function buildFeedbackPayload(values) {
    const raw = values?.content ?? '';
    const content = typeof raw === 'string' ? raw.trim() : '';
    if (!content || isDiscussionContentEmpty(content)) return null;
    const rating = values?.rating;
    if (rating == null || rating < 1 || rating > 5) return null;
    return { content, rating };
}

/** Validation message when content is empty. */
export const FEEDBACK_VALIDATION_ENTER = 'Enter your feedback';
/** Validation message when rating is missing. */
export const FEEDBACK_VALIDATION_RATING = 'Please select a rating (1–5 stars)';

/** Sort by createdAt descending (newest first). */
function sortByNewestFirst(items) {
    if (!Array.isArray(items) || items.length === 0) return items;
    return [...items].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
    });
}

/**
 * Project feedback tab: list and create. RBAC: view/create gated by projects.feedback.view / projects.feedback.create.
 */
export function useProjectFeedback(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_FEEDBACK_VIEW);
    const canCreate = can(PROJECT_FEEDBACK_CREATE);

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const isReviewsEnabled = project?.modules?.isReviews?.enabled !== false;

    const {
        data: feedback = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_FEEDBACK_QUERY_KEY(projectId),
        queryFn: () => getProjectFeedback(projectId),
        enabled: !!projectId && canView && isReviewsEnabled,
    });

    const feedbackNewestFirst = useMemo(() => sortByNewestFirst(Array.isArray(feedback) ? feedback : []), [feedback]);

    const createMutation = useMutation({
        mutationFn: (body) => createProjectFeedback(projectId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const createFeedback = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    /**
     * Validate form values, build payload, and submit. Calls onValidationError(message) if invalid.
     * Page only calls this from form.validateFields().then(...).
     */
    const submitFeedbackFromForm = useCallback(
        (formValues, callbacks = {}) => {
            const payload = buildFeedbackPayload(formValues);
            if (!payload) {
                const message =
                    !formValues?.content?.trim?.() || isDiscussionContentEmpty(String(formValues?.content ?? '').trim())
                        ? FEEDBACK_VALIDATION_ENTER
                        : FEEDBACK_VALIDATION_RATING;
                callbacks.onValidationError?.(message);
                return;
            }
            createFeedback(payload, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [createFeedback]
    );

    return {
        canView,
        canCreate,
        feedback: feedbackNewestFirst,
        isLoading,
        isError,
        refetch,
        createFeedback,
        submitFeedbackFromForm,
        createFeedbackPending: createMutation.isPending,
    };
}
